import React, { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import FullScreenLoader from '../Components/FullScreenLoader/FullScreenLoader'
import { getMe } from '../services/opw.api'
import { getJwtExpiryMs } from '../helpers/jwt'
import { IAuthContext } from '../types'

const AuthContext = React.createContext<IAuthContext>({
  data: {
    token: '',
    user: null,
  },
  updateData: () => {},
})

const AuthProvider = (props: { children: JSX.Element[] | JSX.Element | null }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const isPubUrl =
    location.pathname.slice(0, 7) === '/serviceorder/' && location.pathname.length > 7

  const isRedirectPage = location.pathname === '/redirect'

  const [data, setData] = useState(() => {
    const localStorageData = localStorage.getItem('data')
    if (localStorageData) return JSON.parse(localStorageData)
    else return null
  })

  const [checked, setChecked] = useState(false)

  const updateData = (key: string, value: unknown) => {
    setData((prevData: Record<string, unknown>) => ({
      ...prevData,
      [key]: value,
    }))
  }

  const getUserData = async () => {
    if (!data?.token) {
      if (!isRedirectPage) {
        navigate('/login')
      }
      setChecked(true)
      return
    }
    try {
      const request = await getMe(data?.token)
      if (request?.data?.user) {
        updateData('user', request.data.user)
      }
      setChecked(true)
    } catch (err) {
      console.error(err)
      setChecked(true)
      setData({
        token: '',
        user: null,
      })
      localStorage.setItem(
        'data',
        JSON.stringify({
          token: '',
          user: null,
        }),
      )

      if (!isRedirectPage && location.pathname !== '/login' && location.pathname !== '/login/') {
        navigate('/login')
      }
    }
  }

  useEffect(() => {
    localStorage.setItem('data', JSON.stringify(data))
  }, [data])

  useEffect(() => {
    getUserData()
  }, [])

  useEffect(() => {
    const token = data?.token
    if (!token) return

    const expiryMs = getJwtExpiryMs(token)
    if (expiryMs == null) return

    const expireSession = () => {
      toast.error('Session Expired Please Login Again', {
        toastId: 'error401Toast',
        onClose: () => {
          toast.dismiss('error401Toast')
        },
      })
      setData({ token: '', user: null })
      if (!isRedirectPage && location.pathname !== '/login' && location.pathname !== '/login/') {
        navigate('/login')
      }
    }

    const delay = expiryMs - Date.now()
    if (delay <= 0) {
      expireSession()
      return
    }

    const timer = window.setTimeout(expireSession, delay)
    return () => window.clearTimeout(timer)
  }, [data?.token])

  if (!isRedirectPage && location.pathname !== '/login' && !checked && !isPubUrl) {
    return <FullScreenLoader />
  }

  if (isPubUrl) {
    return (
      <AuthContext.Provider value={{ data, updateData }}>{props.children}</AuthContext.Provider>
    )
  }

  if (
    !isRedirectPage &&
    location.pathname !== '/login' &&
    location.pathname !== '/login/' &&
    !data?.token
  ) {
    return <Navigate to={'/login'} replace />
  }

  return <AuthContext.Provider value={{ data, updateData }}>{props.children}</AuthContext.Provider>
}

export { AuthProvider }
export default AuthContext
